import { convertLexicalToHTML } from '@payloadcms/richtext-lexical/html'

// Payload always returns a lexical object, even when the editor is empty
// (an empty paragraph node). So a truthy check on the field is never enough:
// we convert to HTML and look at what actually comes out.

// Tags that carry content on their own, without any text.
const CONTENT_TAGS = /<(img|picture|iframe|video|audio|source|embed|object|svg|hr|table)\b/i

const htmlCache = new WeakMap<object, string>()

export const richTextToHtml = (content: any): string => {
  if (!content?.root) {
    return ''
  }

  const cached = htmlCache.get(content)
  if (cached !== undefined) {
    return cached
  }

  let html = ''

  try {
    html = convertLexicalToHTML({
      data: content,
      disableContainer: true,
      disableTextAlign: true,
    }) ?? ''
  }
  catch (error) {
    console.warn('richTextToHtml: could not convert lexical content', error)
  }

  htmlCache.set(content, html)

  return html
}

export const richTextToString = (content: any): string => {
  return richTextToHtml(content)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

export const hasRichTextContent = (content: any): boolean => {
  const html = richTextToHtml(content)

  if (!html) {
    return false
  }

  if (CONTENT_TAGS.test(html)) {
    return true
  }

  return richTextToString(content).length > 0
}

const countWords = (text: string): number => {
  const trimmed = text.trim()
  return trimmed ? trimmed.split(/\s+/).length : 0
}

export const richTextWordCount = (content: any): number => {
  return countWords(richTextToString(content))
}

// Truncating the flattened string throws away paragraphs, links and formatting.
// Instead we walk the lexical tree and keep whole nodes until the word budget
// runs out, so the result is still a renderable lexical document.
export const truncateRichText = (content: any, wordLimit: number): any => {
  if (!content?.root || wordLimit <= 0) {
    return content
  }

  let remaining = wordLimit
  let didTruncate = false

  const truncateNode = (node: any): any | null => {
    if (remaining <= 0) {
      return null
    }

    if (typeof node?.text === 'string') {
      const words = node.text.split(/\s+/).filter(Boolean)

      if (words.length <= remaining) {
        remaining -= words.length
        return node
      }

      const kept = words.slice(0, remaining).join(' ')
      remaining = 0
      didTruncate = true

      return { ...node, text: kept }
    }

    if (Array.isArray(node?.children)) {
      return truncateChildren(node)
    }

    return node
  }

  const truncateChildren = (node: any): any | null => {
    const children: any[] = []

    for (const child of node.children ?? []) {
      const truncated = truncateNode(child)

      if (truncated === null) {
        didTruncate = true
        break
      }

      children.push(truncated)
    }

    return children.length ? { ...node, children } : null
  }

  // Nodes we kept whole are still the originals, so clone along the path to the
  // last piece of text rather than mutating the content we were handed.
  const appendEllipsis = (node: any): any | null => {
    if (typeof node?.text === 'string') {
      const text = node.text.trimEnd()

      if (!text) {
        return null
      }

      return text.endsWith('...') ? node : { ...node, text: `${text}...` }
    }

    if (Array.isArray(node?.children)) {
      for (let index = node.children.length - 1; index >= 0; index--) {
        const updated = appendEllipsis(node.children[index])

        if (updated) {
          const children = [...node.children]
          children[index] = updated

          return { ...node, children }
        }
      }
    }

    return null
  }

  const root = truncateChildren(content.root) ?? { ...content.root, children: [] }

  return { ...content, root: didTruncate ? appendEllipsis(root) ?? root : root }
}
